import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChatCircle, ShareNetwork, Flag, Lock, Microphone, MusicNotes, SpeakerSlash, BookmarkSimple, Play, X, Heart, Eye } from "@phosphor-icons/react";
import { api, fileUrl, avatarUrl, uploadFile } from "@/lib/api";
import { formatCount } from "@/lib/formatCount";
import { fileFromAudioBlob } from "@/lib/audioMime";
import { PHOTO_FILTER_CSS } from "@/lib/photoFilters";
import logger from "@/lib/logger";
import WaveformPlayer from "@/components/WaveformPlayer";
import VoiceRecorder from "@/components/VoiceRecorder";
import ReportDialog from "@/components/ReportDialog";
import ShareSheet from "@/components/ShareSheet";
import EmojiReactionsBar, { ReactionsSummary } from "@/components/EmojiReactionsBar";
import EditPostDialog from "@/components/EditPostDialog";
import LikesSheet from "@/components/LikesSheet";
import Emoji from "@/components/Emoji";
import VerifiedBadge from "@/components/VerifiedBadge";
import PostHeader from "@/components/PostHeader";
import PostMetaStrip from "@/components/PostMetaStrip";
import CommentsSheet from "@/components/CommentsSheet";
import PinchZoomImage from "@/components/PinchZoomImage";
import { haptic } from "@/lib/native";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff/60)}m`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h`;
  return `${Math.floor(diff/86400)}d`;
}

export default function PostCard({ post, onCommentAdded, onDeleted }) {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [likes, setLikes] = useState(post.likes_count);
  const [reactionsMap, setReactionsMap] = useState(post.reactions || {});
  const [myReaction, setMyReaction] = useState(post.my_reaction || null);
  const [commentsCount, setCommentsCount] = useState(post.comments_count);
  const [showRecorder, setShowRecorder] = useState(false);
  // When set, the recorder records a *reply* to the given parent comment id.
  // null = top-level voice reply to the post.
  const [replyParentId, setReplyParentId] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [saved, setSaved] = useState(!!post.saved_by_me);
  const [shareOpen, setShareOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [likesSheetOpen, setLikesSheetOpen] = useState(false);
  // Text-caption editing was removed in Phase 45 (Voxly is voice-only).
  // We still surface any pre-existing legacy captions read-only.
  const caption = post.text_caption || "";

  const isOwn = me?.user_id === post.user_id;
  const isVideo = post.media_type === "video";
  const [heartBurst, setHeartBurst] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const musicRef = useRef(null);
  const cardRef = useRef(null);
  const [viewsCount, setViewsCount] = useState(post.views_count || 0);
  // Each post can only ping `/view` once per mount — prevents the same scroll
  // pass from re-incrementing if the user scrolls back and forth.
  const viewPingedRef = useRef(false);

  // View tracker — fires `/posts/{id}/view` after the post has been on
  // screen for ~2 seconds. Backend dedupes per user, so this is safe to
  // fire whenever (the worst case is a no-op response). We restrict to
  // videos for now since photos don't really have a "view" semantic on a
  // feed-style layout, but the endpoint accepts any post id.
  useEffect(() => {
    if (!isVideo || isOwn || viewPingedRef.current) return undefined;
    if (typeof window === "undefined" || !window.IntersectionObserver) return undefined;
    const node = cardRef.current;
    if (!node) return undefined;
    let timer = null;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e?.isIntersecting && e.intersectionRatio > 0.5) {
        timer = setTimeout(async () => {
          if (viewPingedRef.current) return;
          viewPingedRef.current = true;
          try {
            const { data } = await api.post(`/posts/${post.post_id}/view`);
            if (typeof data?.views_count === "number") setViewsCount(data.views_count);
          } catch { /* tracking is best-effort */ }
        }, 2000);
      } else if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }, { threshold: [0, 0.5, 1] });
    io.observe(node);
    return () => { io.disconnect(); if (timer) clearTimeout(timer); };
  }, [isVideo, isOwn, post.post_id]);

  const openReels = () => navigate(`/reels/${post.post_id}`);

  const toggleSave = async () => {
    const wasSaved = saved;
    setSaved(!wasSaved);
    try {
      const { data } = await api.post(`/posts/${post.post_id}/save`);
      toast.success(data.saved ? "Saved to favorites" : "Removed from saved");
    } catch {
      setSaved(wasSaved);
      toast.error("Couldn't save");
    }
  };

  const toggleMusic = () => {
    if (!post.music_url) return;
    if (!musicRef.current) {
      const a = new Audio(post.music_url);
      a.loop = true;
      a.volume = 0.5;
      musicRef.current = a;
    }
    const a = musicRef.current;
    if (musicPlaying) {
      a.pause(); setMusicPlaying(false);
    } else {
      a.play().then(() => setMusicPlaying(true)).catch((err) => {
        logger.warn("post", "music play blocked", err);
        toast.error("Tap again to play music");
      });
    }
  };

  // Auto-sync background music to the voice player. When the user taps Play
  // on the voice clip, the song fades in at the same time; pausing or
  // finishing the voice silences the song. Honors `prefers-reduced-motion`
  // (we still play, just no fade) and survives the singleton "only one
  // voice plays at once" pause from sibling cards.
  const syncMusicToVoice = useCallback((isVoicePlaying) => {
    if (!post.music_url) return;
    if (!musicRef.current) {
      const a = new Audio(post.music_url);
      a.loop = true;
      a.volume = 0.45; // sit slightly under the voice
      musicRef.current = a;
    }
    const a = musicRef.current;
    if (isVoicePlaying) {
      a.play()
        .then(() => setMusicPlaying(true))
        .catch((err) => {
          // Autoplay block is rare here because the voice play() satisfies
          // user-gesture requirements, but we degrade gracefully.
          logger.warn("post", "music auto-play blocked", err);
        });
    } else {
      a.pause();
      setMusicPlaying(false);
    }
  }, [post.music_url]);

  useEffect(() => () => {
    if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
  }, []);

  const triggerLikeOnDoubleTap = () => {
    setHeartBurst(true);
    setTimeout(() => setHeartBurst(false), 700);
    haptic("medium");
    if (myReaction) return; // already reacted, just play the animation
    // Optimistic
    onReactionChange("❤️");
    api.post(`/posts/${post.post_id}/reactions`, { emoji: "❤️" }).catch(() => {
      onReactionChange(null);
      toast.error("Couldn't react");
    });
  };

  const onReactionChange = (emoji) => {
    setReactionsMap((prev) => {
      const next = { ...prev };
      if (myReaction && next[myReaction]) {
        next[myReaction] = Math.max(0, next[myReaction] - 1);
        if (next[myReaction] === 0) delete next[myReaction];
      }
      if (emoji) next[emoji] = (next[emoji] || 0) + 1;
      return next;
    });
    setMyReaction(emoji);
    setLikes((n) => {
      if (myReaction && emoji) return n;
      if (!myReaction && emoji) return n + 1;
      if (myReaction && !emoji) return Math.max(0, n - 1);
      return n;
    });
  };

  const openComments = async () => {
    setShowComments((v) => !v);
    if (comments == null) {
      setLoadingComments(true);
      try {
        const { data } = await api.get(`/posts/${post.post_id}/comments`);
        setComments(data);
      } catch { toast.error("Failed to load comments"); setComments([]); }
      finally { setLoadingComments(false); }
    }
  };

  const handleVoiceCommentRecorded = async ({ blob, duration }) => {
    const parentForThisRecording = replyParentId;
    try {
      const file = fileFromAudioBlob(blob, "vc");
      const up = await uploadFile(file, "audio");
      const body = { audio_path: up.path, duration };
      if (parentForThisRecording) body.parent_comment_id = parentForThisRecording;
      await api.post(`/posts/${post.post_id}/comments`, body);
      setShowRecorder(false);
      setReplyParentId(null);
      setCommentsCount((n) => n + 1);
      onCommentAdded?.();
      if (showComments) {
        const { data } = await api.get(`/posts/${post.post_id}/comments`);
        setComments(data);
      }
      toast.success(parentForThisRecording ? "Reply posted" : "Voice comment posted");
    } catch (e) {
      if (e?.response?.status === 429) toast.error("Slow down — too many comments");
      else toast.error("Failed to send voice comment");
    }
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/posts/${post.post_id}`);
      toast.success("Post deleted");
      onDeleted?.(post.post_id);
    } catch { toast.error("Delete failed"); }
  };

  const openReport = (type, id) => {
    setReportTarget({ type, id });
    setReportOpen(true);
  };

  const mediaSrc = post.media_path ? fileUrl(post.media_path) : null;
  const voiceSrc = post.voice_path ? fileUrl(post.voice_path) : null;
  const blurred = post.blurred;

  return (
    <article
      ref={cardRef}
      className="relative bg-card rounded-2xl overflow-hidden mx-2.5 my-3 shadow-sm"
      data-testid={`post-card-${post.post_id}`}
    >
      {/* Header */}
      <PostHeader
        post={post}
        isOwn={isOwn}
        onDelete={deletePost}
        onReport={openReport}
        onEdit={() => setEditOpen(true)}
        onHide={async () => {
          try {
            const { data } = await api.post(`/posts/${post.post_id}/hide`);
            if (data.hidden) {
              toast.success("Post hidden from your feed");
              if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("voxly:post-hidden", { detail: post.post_id }));
              }
            }
          } catch { toast.error("Couldn't hide"); }
        }}
        onCopyLink={async () => {
          const url = `${window.location.origin}/p/${post.post_id}`;
          try {
            await navigator.clipboard.writeText(url);
            toast.success("Link copied");
          } catch {
            // Older browsers / iframes — toast it for manual copy.
            toast.success(url);
          }
        }}
      />

      {/* Voice caption — Facebook/Instagram style: a slim labeled voice pill
          that visually belongs to the post author. The mini avatar + first-
          name chip makes it unambiguous that THIS voice belongs to THIS
          author, even when posts stack tightly in the feed. */}
      {voiceSrc && !blurred && (
        <div className="px-3 mt-1 pb-2 relative" data-testid={`voice-caption-wrap-${post.post_id}`}>
          <div className="rounded-2xl bg-primary/8 border border-primary/25 px-2 py-1.5 flex items-center gap-2">
            <Link
              to={`/u/${post.user_id}`}
              className="shrink-0 flex items-center gap-1.5 pr-1.5 border-r border-primary/20"
              aria-label={`Voice by ${post.user_name}`}
            >
              <img
                src={avatarUrl(post.user_picture)}
                alt=""
                className="h-6 w-6 rounded-full object-cover ring-1 ring-primary/30"
                loading="lazy"
                decoding="async"
              />
              <span className="text-[10px] font-bold text-foreground/90 max-w-[70px] truncate hidden sm:inline">
                {post.user_name?.split(" ")[0] || "Voice"}
              </span>
            </Link>
            <div className="flex-1 min-w-0">
              <WaveformPlayer src={voiceSrc} duration={post.duration} compact testid={`voice-caption-${post.post_id}`} onPlayStateChange={syncMusicToVoice} />
            </div>
          </div>
        </div>
      )}

      {/* Media (full-bleed for IG-like feel) */}
      {blurred ? (
        <div className="aspect-[4/5] flex flex-col items-center justify-center gap-2 text-center px-6 bg-secondary/40">
          <Lock size={28} className="text-muted-foreground" />
          <p className="text-sm font-semibold">This account is private</p>
          <p className="text-xs text-muted-foreground">Follow {post.user_name} to see their posts.</p>
        </div>
      ) : mediaSrc ? (
        <div className="bg-black relative select-none" onDoubleClick={triggerLikeOnDoubleTap} data-testid={`post-media-${post.post_id}`}>
          {isVideo ? (
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              data-testid={`open-fullscreen-${post.post_id}`}
              className="block w-full text-left group"
              aria-label="View fullscreen"
            >
              <video
                src={mediaSrc}
                muted
                playsInline
                preload="metadata"
                className="w-full max-h-[58vh] object-cover pointer-events-none"
                style={{ filter: PHOTO_FILTER_CSS[post.photo_filter || "none"] }}
                data-testid="post-video"
              />
              <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="h-16 w-16 rounded-full bg-black/55 backdrop-blur-md flex items-center justify-center text-white group-hover:scale-110 transition">
                  <Play size={28} weight="fill" />
                </span>
              </span>
              <span
                onClick={(e) => { e.stopPropagation(); openReels(); }}
                role="link"
                tabIndex={0}
                className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-black/55 backdrop-blur-md text-[10px] font-mono uppercase tracking-widest text-white/90 cursor-pointer hover:bg-black/75"
                data-testid={`open-reels-pill-${post.post_id}`}
              >
                Open in Reels
              </span>
              {viewsCount > 0 && (
                <span
                  className="absolute bottom-2 left-2 px-2 py-1 rounded-full bg-black/55 backdrop-blur-md text-[10px] font-mono text-white/90 pointer-events-none inline-flex items-center gap-1"
                  data-testid={`post-views-${post.post_id}`}
                >
                  <Eye size={12} weight="fill" />
                  <span className="tabular-nums">{formatCount(viewsCount)}</span>
                  <span className="uppercase tracking-widest">views</span>
                </span>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              data-testid={`open-lightbox-${post.post_id}`}
              className="block w-full text-left"
              aria-label="View photo"
            >
              <img
                src={mediaSrc}
                alt=""
                className="w-full max-h-[58vh] object-cover pointer-events-none"
                style={{ filter: PHOTO_FILTER_CSS[post.photo_filter || "none"] }}
                data-testid="post-image"
                loading="lazy"
                decoding="async"
              />
            </button>
          )}
          {heartBurst && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid={`heart-burst-${post.post_id}`}>
              <span className="text-[120px] drop-shadow-[0_0_20px_rgba(255,80,80,0.9)] animate-[heartBurst_700ms_ease-out]" style={{ filter: "saturate(1.3)" }}>❤️</span>
            </div>
          )}
        </div>
      ) : null}

      {/* Action row */}
      <div className="px-4 pt-3 pb-1 flex items-center gap-1">
        <EmojiReactionsBar
          postId={post.post_id}
          myReaction={myReaction}
          totalReactors={likes}
          reactionsMap={reactionsMap}
          onChange={onReactionChange}
          onCountClick={() => setLikesSheetOpen(true)}
          disabled={blurred}
        />
        <button
          onClick={openComments}
          data-testid={`comments-button-${post.post_id}`}
          disabled={blurred}
          aria-label="Comments"
          className="h-11 w-11 rounded-full flex items-center justify-center text-foreground hover:text-primary hover:bg-secondary/60 disabled:opacity-40 tap-press"
        >
          <ChatCircle size={26} weight="bold" />
        </button>
        <button
          onClick={() => setShareOpen(true)}
          disabled={blurred}
          aria-label="Share"
          data-testid={`share-button-${post.post_id}`}
          className="h-11 w-11 rounded-full flex items-center justify-center text-foreground hover:text-primary hover:bg-secondary/60 disabled:opacity-40 tap-press"
        >
          <ShareNetwork size={24} weight="bold" />
        </button>
        <button
          onClick={toggleSave}
          disabled={blurred}
          aria-label={saved ? "Unsave" : "Save"}
          data-testid={`save-button-${post.post_id}`}
          className={`h-11 w-11 rounded-full flex items-center justify-center hover:bg-secondary/60 disabled:opacity-40 tap-press ${saved ? "text-yellow-400" : "text-foreground hover:text-primary"}`}
        >
          <BookmarkSimple size={24} weight={saved ? "fill" : "bold"} />
        </button>
        <button
          onClick={() => setShowRecorder(true)}
          data-testid={`voice-comment-button-${post.post_id}`}
          disabled={blurred}
          className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-bold tracking-wide glow-hover disabled:opacity-40 min-h-[40px] active:scale-95 transition shadow-lg"
        >
          <Microphone size={18} weight="fill" /> Reply
        </button>
      </div>

      {/* Stats */}
      <div className="px-4 pt-1 text-sm">
        <ReactionsSummary reactionsMap={reactionsMap} totalReactors={likes} myReaction={myReaction} topReactors={post.top_reactors} onClick={() => setLikesSheetOpen(true)} />
        {commentsCount > 0 && (
          <button onClick={openComments} className="text-muted-foreground text-xs hover:text-foreground mt-1 block">
            View all {commentsCount} voice {commentsCount === 1 ? "reply" : "replies"}
          </button>
        )}
        {/* Author-only insights pill — lets the post author see at-a-glance
            engagement without scrolling through reactions. Only renders on
            posts they own. Tap opens the likers sheet for the deeper drill. */}
        {isOwn && (likes > 0 || commentsCount > 0 || post.saves_count > 0 || viewsCount > 0) && (
          <button
            onClick={() => setLikesSheetOpen(true)}
            data-testid={`post-insights-${post.post_id}`}
            className="mt-1.5 inline-flex items-center gap-3 px-3 py-1 rounded-full bg-primary/10 border border-primary/25 text-[11px] font-mono uppercase tracking-widest text-primary/90 hover:bg-primary/15 transition"
          >
            <span><span className="font-bold text-foreground">{formatCount(likes)}</span> reactions</span>
            <span className="text-primary/40">·</span>
            <span><span className="font-bold text-foreground">{formatCount(commentsCount)}</span> {commentsCount === 1 ? "reply" : "replies"}</span>
            {isVideo && (
              <>
                <span className="text-primary/40">·</span>
                <span><span className="font-bold text-foreground">{formatCount(viewsCount)}</span> views</span>
              </>
            )}
            {post.saves_count > 0 && (
              <>
                <span className="text-primary/40">·</span>
                <span><span className="font-bold text-foreground">{formatCount(post.saves_count)}</span> saves</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Background music chip (TikTok-style scrolling marquee) */}
      {post.music_url && !blurred && (
        <button
          onClick={toggleMusic}
          data-testid={`music-toggle-${post.post_id}`}
          className="px-4 mt-2 w-full flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition group"
          title={musicPlaying ? "Pause music" : "Play music"}
        >
          {musicPlaying ? (
            <MusicNotes size={14} weight="fill" className="text-primary animate-pulse" />
          ) : (
            <SpeakerSlash size={14} weight="bold" className="opacity-60" />
          )}
          <span className="font-semibold truncate group-hover:text-primary" data-testid={`music-title-${post.post_id}`}>
            {post.music_title || "Music"}
          </span>
          <span className="ml-auto text-[10px] font-mono opacity-60">{musicPlaying ? "PLAYING WITH VOICE" : "AUTO-PLAYS WITH VOICE"}</span>
        </button>
      )}

      {/* Metadata strip (Phase 72) — tap-driven context attached by the
          author at post time: location, feeling, tagged people, hashtags.
          Hashtags route to /h/:tag and mentions to /u/:user_id. */}
      {!blurred && <PostMetaStrip post={post} />}

      {/* Text caption */}
      {caption && !blurred && (
        <p className="px-4 mt-2 text-sm">
          <Link to={`/u/${post.user_id}`} className="font-semibold mr-1.5 hover:underline inline-flex items-center gap-1">
            {post.user_name}
            {post.user_verified && <VerifiedBadge size={12} />}
          </Link>
          <span className="text-foreground/90">{caption}</span>
        </p>
      )}

      {/* Comments — Facebook-style bottom sheet. Opened from the comments
          button in the action bar or the "View all N voice replies" link
          under the stats row. */}
      <CommentsSheet
        open={showComments}
        onClose={() => setShowComments(false)}
        comments={comments}
        loading={loadingComments}
        postAuthorName={post.user_name}
        renderMention={renderTranscriptWithMentions}
        onRecord={(parentId) => {
          setReplyParentId(parentId || null);
          setShowRecorder(true);
        }}
        onReport={(commentId) => openReport("comment", commentId)}
      />

      {/* Section divider */}
      <div className="h-px bg-border/60 mt-4" />

      <VoiceRecorder
        open={showRecorder}
        onClose={() => { setShowRecorder(false); setReplyParentId(null); }}
        onComplete={handleVoiceCommentRecorded}
        title={replyParentId ? "Voice reply" : "Voice comment"}
        subtitle={
          replyParentId
            ? (() => {
                const parent = comments?.find((c) => c.comment_id === replyParentId);
                return parent ? `Replying to @${parent.user_name}` : null;
              })()
            : null
        }
      />
      <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} targetType={reportTarget?.type} targetId={reportTarget?.id} targetLabel={reportTarget?.type === "post" ? `Post by ${post.user_name}` : `Comment`} />
      <EditPostDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        post={post}
        onSaved={() => {
          // Tell the parent feed to refetch so the updated post (new voice,
          // new filter, new music, new audience) renders immediately.
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("voxly:post-edited", { detail: post.post_id }));
          }
        }}
      />
      <LikesSheet
        postId={post.post_id}
        open={likesSheetOpen}
        onClose={() => setLikesSheetOpen(false)}
      />
      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        postId={post.post_id}
        postUrl={`${window.location.origin}/p/${post.post_id}`}
        authorName={post.user_name}
        mediaUrl={post.media_path ? fileUrl(post.media_path) : null}
        mediaKind={post.media_type}
      />
      <PhotoLightbox
        open={lightboxOpen && !!mediaSrc}
        onClose={() => setLightboxOpen(false)}
        mediaSrc={mediaSrc}
        isVideo={isVideo}
        filter={PHOTO_FILTER_CSS[post.photo_filter || "none"]}
        myReaction={myReaction}
        likes={likes}
        commentsCount={commentsCount}
        saved={saved}
        post={post}
        voiceSrc={voiceSrc}
        duration={post.duration}
        onVoicePlayStateChange={syncMusicToVoice}
        onReact={() => {
          // delegate to the existing reactions API by simulating a heart tap.
          // Re-uses the heart-burst path so the lightbox feels alive.
          triggerLikeOnDoubleTap();
        }}
        onComment={() => {
          // Keep the lightbox open — the comments sheet slides up over it so
          // the user stays in the immersive fullscreen experience.
          openComments();
        }}
        onShare={() => {
          setLightboxOpen(false);
          setShareOpen(true);
        }}
        onSave={() => {
          toggleSave();
        }}
      />
    </article>
  );
}

/** Instagram-style fullscreen photo viewer with action toolbar.
 *  Tapping the photo on the feed opens this; the user can react, voice-reply,
 *  or share without leaving the post. */
function PhotoLightbox({ open, onClose, mediaSrc, isVideo, filter, myReaction, likes, commentsCount, saved, onReact, onComment, onShare, onSave, post, voiceSrc, duration, onVoicePlayStateChange }) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black flex flex-col"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      {/* Top-left close */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid="lightbox-close"
        className="absolute top-4 left-4 z-20 h-10 w-10 rounded-full bg-white/10 backdrop-blur-md text-white flex items-center justify-center tap-press"
      >
        <X size={22} />
      </button>

      {/* Author chip top-center */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full pl-1 pr-3 py-1 max-w-[60%]">
        <img src={avatarUrl(post.user_picture)} alt="" className="h-7 w-7 rounded-full object-cover" />
        <span className="text-sm font-bold text-white truncate">{post.user_name}</span>
      </div>

      {/* Media area — fills the screen, contained so we never crop. */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo ? (
          <video
            src={mediaSrc}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-full w-auto h-auto"
            style={{ filter }}
            data-testid="lightbox-video"
          />
        ) : (
          <PinchZoomImage
            src={mediaSrc}
            alt=""
            style={{ filter }}
            testid="lightbox-image"
          />
        )}
      </div>

      {/* Right-side action rail — TikTok-style vertical stack. */}
      <div
        className="absolute right-3 bottom-32 z-20 flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-action-rail"
      >
        <ActionRailButton
          icon={myReaction ? <Emoji char={myReaction} size={28} /> : <Heart size={26} weight={myReaction ? "fill" : "bold"} />}
          onClick={onReact}
          label={String(likes)}
          active={!!myReaction}
          testid="lightbox-react"
        />
        <ActionRailButton
          icon={<ChatCircle size={26} weight="bold" />}
          onClick={onComment}
          label={commentsCount > 0 ? String(commentsCount) : "Reply"}
          testid="lightbox-comment"
        />
        <ActionRailButton
          icon={<ShareNetwork size={24} weight="bold" />}
          onClick={onShare}
          label="Share"
          testid="lightbox-share"
        />
        {onSave && (
          <ActionRailButton
            icon={<BookmarkSimple size={24} weight={saved ? "fill" : "bold"} />}
            onClick={onSave}
            label={saved ? "Saved" : "Save"}
            active={saved}
            testid="lightbox-save"
          />
        )}
      </div>

      {/* Bottom — voice caption (if present) + recent voice replies prompt. */}
      <div
        className="relative z-10 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-12 bg-gradient-to-t from-black via-black/70 to-transparent"
        onClick={(e) => e.stopPropagation()}
      >
        {voiceSrc && (
          <div className="mb-3" data-testid="lightbox-voice-caption">
            <WaveformPlayer
              src={voiceSrc}
              duration={duration}
              compact
              testid={`lightbox-voice-${post.post_id}`}
              onPlayStateChange={onVoicePlayStateChange}
            />
          </div>
        )}
        <button
          onClick={onComment}
          data-testid="lightbox-open-comments"
          className="w-full flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white tap-press"
        >
          <ChatCircle size={16} weight="bold" className="opacity-70" />
          <span className="text-sm text-white/85 flex-1 text-left">
            {commentsCount > 0
              ? `View all ${commentsCount} voice ${commentsCount === 1 ? "reply" : "replies"}`
              : "Be the first to leave a voice reply"}
          </span>
        </button>
      </div>
    </div>
  );
}

/**
 * Single button in the TikTok-style action rail on the right side of the
 * fullscreen media viewer. Wraps icon + count/label in a stacked layout.
 */
function ActionRailButton({ icon, onClick, label, active, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex flex-col items-center gap-1 text-white tap-press"
    >
      <span
        className={`h-12 w-12 rounded-full flex items-center justify-center shadow-xl ${
          active
            ? "bg-primary/30 ring-2 ring-primary"
            : "bg-white/10 backdrop-blur-md border border-white/20"
        }`}
      >
        {icon}
      </span>
      <span className="text-[11px] font-bold tabular-nums max-w-[60px] truncate">{label}</span>
    </button>
  );
}


/**
 * Inline-render a Whisper transcript with detected @-mentions turned into
 * clickable chips. We do a single-pass scan over the transcript and replace
 * each `@handle` substring (matched against the backend-resolved `mentions`
 * list) with a styled <Link> to the user's profile. Mentions outside the
 * resolved list stay as plain text so we never link to nonexistent users.
 */
function renderTranscriptWithMentions(transcript, mentions) {
  if (!transcript) return null;
  if (!mentions || mentions.length === 0) return transcript;
  // Build a lookup keyed by lowercase handle.
  const byHandle = new Map();
  mentions.forEach((m) => { if (m?.username) byHandle.set(m.username.toLowerCase(), m); });
  // Greedy regex on @handle tokens (3-30 chars, alnum + . _).
  const parts = [];
  let lastIdx = 0;
  const re = /@([a-zA-Z0-9_.]{3,30})/g;
  let match;
  while ((match = re.exec(transcript)) !== null) {
    if (match.index > lastIdx) parts.push(transcript.slice(lastIdx, match.index));
    const handle = match[1].toLowerCase();
    const m = byHandle.get(handle);
    if (m) {
      parts.push(
        <Link
          key={`m-${match.index}`}
          to={`/u/${m.user_id}`}
          data-testid={`transcript-mention-${m.user_id}`}
          className="text-primary not-italic font-semibold hover:underline"
        >@{m.username}</Link>
      );
    } else {
      parts.push(match[0]);
    }
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < transcript.length) parts.push(transcript.slice(lastIdx));
  return parts;
}
